<template>
    <app-layout>
        <template #icon> <HeaderIcon /> </template>
        <template #title> {{ $t("event_types.title") }} </template>
        <template #description> {{ $t("event_types.description") }} </template>
        <template #actions>
            <CreateEventTypeModal
                v-model="modalOpened"
                :integrations="integrations"
                :personal-event-types="personalEventTypes"
            >
                <AddButton
                    :paid-only="
                        personalEventTypes.length !== 0 && !hasMultipleTeams
                    "
                    feature="unlimited_events"
                    data-cy="create-new-event-type"
                    @click="modalOpened = true"
                >
                    {{ $t("event_types.new_event") }}</AddButton
                >
            </CreateEventTypeModal>
        </template>
        <div class="px-4 pt-4 sm:px-6 sm:pt-8">
            <div v-if="hasMultipleTeams" class="flex w-full justify-end">
                <select
                    v-model="selectedTeamId"
                    class="focus-visible:ring-iron-950 dark:bg-iron-950 dark:ring-offset-iron-950 h-10 w-48 rounded-lg border-iron-200 bg-white px-3 py-2 text-sm ring-offset-white file:text-sm file:font-medium placeholder:text-iron-500 dark:border-iron-800 dark:placeholder:text-iron-400 dark:focus-visible:ring-iron-300 flex border file:border-0 file:bg-transparent focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <option :value="undefined">
                        {{ $t("event_types.all_teams") }}
                    </option>
                    <option
                        v-for="team in teams"
                        :key="team.id"
                        :value="team.id"
                    >
                        {{ getTeamName(team) }}
                    </option>
                </select>
            </div>

            <template v-if="eventTypes.length > 0">
                <div :class="[hasMultipleTeams ? '-mt-9' : '']">
                    <div v-for="(team, i) in teamsWithEventTypes" :key="i">
                        <h2
                            v-if="hasMultipleTeams"
                            class="text-lg font-medium max-w-[150px] truncate text-[#18181B]"
                        >
                            {{ getTeamName(team) }}
                        </h2>

                        <div v-if="team.eventTypes.length === 0">
                            <p class="mb-8 mt-10 text-sm text-iron-500">
                                {{ $t("event_types.team_has_no_event_type") }}
                            </p>
                        </div>
                        <div v-else class="mt-8">
                            <ZaptimeEvenTypePreview
                                v-for="eventType in team.eventTypes"
                                :key="eventType.id"
                                :event-type="eventType"
                            ></ZaptimeEvenTypePreview>
                        </div>
                    </div>
                </div>
            </template>

            <template v-else>
                <EmptyStateWithIcon
                    :title="$t('event_types.empty_state.title')"
                    :description="$t('event_types.empty_state.description')"
                >
                    <template #icon> <EventTypeHeaderLogo /> </template>
                </EmptyStateWithIcon>
            </template>
        </div>
    </app-layout>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import AppLayout from "../../../../Layouts/AppLayout.vue";
import CreateEventTypeModal from "./components/CreateEventTypeModal.vue";
import ZaptimeEvenTypePreview from "./EventTypePreview.vue";
import AddButton from "../Atomic/AddButton.vue";
import { usePage } from "@inertiajs/vue3";
import HeaderIcon from "./components/HeaderIcon.vue";
import type { EventType } from "./types/EventType";
import type { Team } from "../Teams/types/Team";
import { useI18n } from "vue-i18n";
import { EmptyStateWithIcon } from "@/components/empty-state-with-icon";
import EventTypeHeaderLogo from "./components/EventTypeHeaderLogo.svg";
interface TeamWithEventTypes extends Team {
    eventTypes: EventType[];
}

const props = defineProps<{
    integrations: any[];
    eventTypes: EventType[];
    locationIntegrations: {
        google: boolean;
        zoom: boolean;
    };
}>();

const page = usePage();
const { t } = useI18n();

const modalOpened = ref(false);

const selectedTeamId = ref<number>();

function getTeamName(team: Team) {
    const pageProps = usePage().props;

    // @ts-expect-error
    if (pageProps.personalTeam.id === team.id) {
        return t("event_types.personal");
    }

    return team.name;
}

const teamsWithEventTypes = computed((): TeamWithEventTypes[] => {
    const eventTypes = props.eventTypes;

    const allTeamsWithEventTypes = teams.value.map((team) => {
        return {
            ...team,
            eventTypes: eventTypes.filter(
                (eventType) => eventType.team_id === team.id,
            ),
        };
    });

    if (selectedTeamId.value === undefined) {
        return allTeamsWithEventTypes;
    }

    const onlySelectedTeamOrAll = allTeamsWithEventTypes.filter((team) => {
        return team.id === selectedTeamId.value;
    });

    return onlySelectedTeamOrAll;
});

const hasMultipleTeams = computed(() => {
    return teams.value.length > 1;
});

const teams = computed(() => {
    return page.props.allTeams as Team[];
});

const personalEventTypes = computed(() => {
    return props.eventTypes.filter(
        (eventType) => eventType.team.personal_team === true,
    );
});
</script>
